import gql from 'graphql-tag'
import { FRAGMENT_PRODUCT_BASE_WITH_COUPON } from './product'
import { PRODUCT_ESTIMATED_DELIVERY_FRAGMENT } from '~/graphql/gql/product/fragments'
import {
  GQLQueryGetPageBannersArgs,
  GQLQueryGetPositionedPageBannersArgs,
} from '~/graphql/__generated__/interfaces'

export type GetPageBannersQuery = GQLQueryGetPageBannersArgs & {
  isMobile?: boolean
}

export type GetPositionedPageBannersQuery = GQLQueryGetPositionedPageBannersArgs & {
  isMobile?: boolean
}

const BANNER_SLIDE_BASE_FRAGMENT = gql`
  fragment bannerSlideBase on PageBannerSlide {
    slideId
    position
    gaTag
    urlNewWindow
  }
`

const BANNER_SLIDE_IMAGE_FRAGMENT = gql`
  fragment bannerSlideImage on PageBannerSlideImage {
    ...bannerSlideBase
    url
    text
    textColor
    mediaId
    mediaIdMobile
    video
    header
    v2
  }
`

const TOP_ICONS_BANNER_FRAGMENT = gql`
  fragment topIconsBanner on PageBannerTopIconsBanner {
    bannerAttributes {
      contentCentered
      enableSliding
      header
      headerColor
      headerCentered
    }
    bannerSlides {
      url
      firstRow
      secondRow
      mediaId
      mediaIdMobile
      ...bannerSlideBase
    }
  }
`

const CMS_CAROUSEL_WITH_IMAGE_FRAGMENT = gql`
  fragment cmsCarouselWithImage on PageBannerCmsCarouselWithImage {
    position
    bannerId
    bannerAttributes {
      version
      gridConfig {
        value
        imageWidth
        carouselWidth
      }
      bgConfMobile {
        titleColor
        bgSize
        bgRepeat
        bgPosition
        bgColor
        mediaId
      }
      bgConfDesktop {
        titleColor
        bgSize
        bgRepeat
        bgPosition
        bgColor
        mediaId
      }
      header
      headerColor
      headerCentered
    }
    items(isMobile: $isMobile) {
      ... on Product {
        ...productBaseWithCoupon
        mainVariant {
          packageGroupId
          estimatedDeliveries {
            ...productEstimatedDeliveryFragment
          }
        }
        rank {
          count
          rank
        }
      }
    }
    bannerSlides {
      ...bannerSlideImage
    }
  }

  ${PRODUCT_ESTIMATED_DELIVERY_FRAGMENT}
`

const PAGE_BANNERS = `
  ... on PageBannerBase {
    bannerId
    position
  }
  ... on PageBannerBanners {
    bannerSlides {
      ...bannerSlideImage
    }
    bannerAttributes {
      header
      headerColor
      headerCentered
    }
  }
  ... on PageBannerCarousel {
    bannerAttributes {
      autoplay
      imgBorderRadius
      header
      headerColor
      headerCentered
    }
    bannerSlides {
      ...bannerSlideImage
    }
  }
  ... on PageBannerUspBanner {
    gaTag
    bannerAttributes {
      mediaId
      iconsBackgroundColor
      bannerBackgroundColor
      bannerBackgroundRepeat
      pointTextColor
      button {
        text
        url
      }
      points {
        title
        text
        mediaId
      }
      header
      headerColor
      headerCentered
    }
  }
  ... on PageBannerSlimBanner {
    bannerAttributes {
      borderRadius
      backgroundSize
      backgroundPosition
      header
      headerColor
      headerCentered
    }
    bannerSlides {
      ...bannerSlideImage
    }
  }
  ... on PageBannerDailyDealBanner {
    bannerId
    gaTag
    position
    bannerAttributes {
      header
      backgroundColor
      validTo
      productId
      productTitle
      productHeadline
      header
      headerColor
      headerCentered
    }
  }
  ... on PageBannerBannerFarm {
    bannerAttributes {
      backgroundColor
      header
      headerColor
      headerCentered
    }
    bannerSlides {
      ... on PageBannerSlideImage {
        ...bannerSlideImage
      }
    }
  }
  ... on PageBannerHpBanner {
    gaTag
    bannerSlides {
      ...bannerSlideImage
      backgroundColor
    }
    bannerAttributes {
      header
      headerColor
      headerCentered
    }
    topIcons {
      ...topIconsBanner
    }
  }
  ... on PageBannerTopIconsBanner {
    ...topIconsBanner
  }
  ... on PageBannerCmsCarouselWithImage {
    ...cmsCarouselWithImage
  }
  ... on PageBannerInspoBanner {
    bannerSlides {
      ...bannerSlideImage
    }
    bannerAttributes {
      header
      headerColor
      headerCentered
    }
  }
`

export const GET_PAGE_BANNERS = gql`
  fragment infoImageBanner on InfoImageBanner {
    url
    urlNewWindow
    closable
    closeButtonColor
    backgroundColor
    textColor
    alignLeft
    gaTag
    autoplayDuration
    images {
      ...infoBannerImage
    }
  }

  fragment infoBannerImage on InfoBannerImage {
    url
    urlNewWindow
    mediaId
    mobileMediaId
    backgroundColor
  }

  fragment infoPointsBanner on InfoPointsBanner {
    heading
    subheading
    url
    urlNewWindow
    gaTag
    closable
    closeButtonColor
    backgroundColor
    textColor
    alignLeft
    mediaId
    mobileMediaId
    points {
      ...infoBannerPoint
    }
  }

  fragment infoBannerPoint on InfoBannerPoint {
    url
    urlNewWindow
    title
    mediaId
  }

  fragment upperRightBanner on UpperRightCornerBanner {
    bannerId
    slideId
    mediaId
    url
    urlNewWindow
    gaTag
  }

  fragment backgroundBanner on BackgroundBanner {
    bannerId
    url
    urlNewWindow
    skyscraperImage
    backgroundImage
    backgroundColor
    gaTag
  }

  query getPageBanners($pageUrl: ID!) {
    getPageBanners(pageUrl: $pageUrl) {
      pageId
      upperRightCornerBanner {
        ...upperRightBanner
      }
      infoBanner {
        ... on InfoImageBanner {
          ...infoImageBanner
        }
        ... on InfoPointsBanner {
          ...infoPointsBanner
        }
      }
      backgroundBanner {
        ...backgroundBanner
      }
      headerBanner {
        bannerId
        url
        gaTag
        urlNewWindow
        mediaId
        mobileMediaId
        headerText
        textColor
        backgroundColor
        backgroundRepeat
      }
    }
  }
`

export const GET_BANNER_POSITIONS_TYPES_AND_ITEMS = gql`
  query GetBannerPositionsTypesAndItems(
    $pageUrl: ID!
    $query: GetPageBannerBannersQueryInput!
    $isMobile: Boolean = false
  ) {
    getPositionedPageBanners(pageUrl: $pageUrl, query: $query) {
      ... on PageBannerBase {
        position
        __typename
      }
      ... on PageBannerBanners {
        position
        __typename
      }
      ... on PageBannerCarousel {
        position
        __typename
      }
      ... on PageBannerUspBanner {
        position
        __typename
      }
      ... on PageBannerSlimBanner {
        position
        __typename
      }
      ... on PageBannerDailyDealBanner {
        position
        __typename
      }
      ... on PageBannerBannerFarm {
        position
        __typename
      }
      ... on PageBannerHpBanner {
        position
        __typename
      }
      ... on PageBannerTopIconsBanner {
        position
        __typename
      }
      ... on PageBannerCmsCarouselWithImage {
        position
        __typename
        bannerId
        bannerAttributes {
          version
          header
        }
        items(isMobile: $isMobile) {
          ... on Product {
            id
          }
        }
      }
      ... on PageBannerInspoBanner {
        position
        __typename
      }
    }
  }
`

export const GET_POSITIONED_PAGE_BANNERS = gql`
  ${BANNER_SLIDE_BASE_FRAGMENT}
  ${BANNER_SLIDE_IMAGE_FRAGMENT}
  ${TOP_ICONS_BANNER_FRAGMENT}
  ${CMS_CAROUSEL_WITH_IMAGE_FRAGMENT}

  query(
    $pageUrl: ID!
    $query: GetPageBannerBannersQueryInput!
    $isMobile: Boolean = false
  ) {
    getPositionedPageBanners(pageUrl: $pageUrl, query: $query) {
      ${PAGE_BANNERS}
    }
  }

  ${FRAGMENT_PRODUCT_BASE_WITH_COUPON}
`
